import { useEffect, useState } from 'react';
import { useAuth } from '../lib/AuthContext.js';
import { ApiClient } from '../lib/api.js';

interface Rule { id: string; text: string }
interface Persona { id: string; label: string; short: string }

export function Settings() {
  const { user, getToken } = useAuth();
  const api = new ApiClient(getToken);

  // Persona state
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersonas, setSelectedPersonas] = useState<Set<string>>(new Set());
  const [personaCustom, setPersonaCustom] = useState('');
  const [personaBusy, setPersonaBusy] = useState(false);
  const [personaSaved, setPersonaSaved] = useState(false);

  // Ranking-rule state
  const [playbook, setPlaybook] = useState<{ hardRules: Rule[]; softPrinciples: Rule[]; version: number } | null>(null);
  const [disabled, setDisabled] = useState<Set<string>>(new Set());
  const [extraDos, setExtraDos] = useState('');
  const [extraDonts, setExtraDonts] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, p, m] = await Promise.all([
          api.myRanking(),
          api.listPersonas(),
          api.me(),
        ]);
        setPlaybook(r.playbook);
        setDisabled(new Set(r.overrides?.disabledRuleIds ?? []));
        setExtraDos((r.overrides?.extraDosAndDonts?.dos ?? []).join('\n'));
        setExtraDonts((r.overrides?.extraDosAndDonts?.donts ?? []).join('\n'));
        setPersonas(p.personas ?? []);
        setSelectedPersonas(new Set((m as any)?.user?.personas ?? []));
        setPersonaCustom((m as any)?.user?.personaCustom ?? '');
      } catch (e: any) { setErr(e.message); }
    })();
    /* eslint-disable-next-line */
  }, []);

  function toggle(id: string) {
    setDisabled(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setSaved(false);
  }
  function togglePersona(id: string) {
    setSelectedPersonas(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setPersonaSaved(false);
  }

  async function savePersona() {
    setPersonaBusy(true); setErr(null);
    try {
      await api.setPersona({
        personas: Array.from(selectedPersonas),
        personaCustom: personaCustom.trim(),
      });
      setPersonaSaved(true);
    } catch (e: any) { setErr(e.message); }
    setPersonaBusy(false);
  }

  async function save() {
    setBusy(true); setErr(null);
    try {
      await api.updateMyRanking({
        disabledRuleIds: Array.from(disabled),
        extraDosAndDonts: {
          dos: extraDos.split('\n').map(s => s.trim()).filter(Boolean),
          donts: extraDonts.split('\n').map(s => s.trim()).filter(Boolean),
        },
      });
      setSaved(true);
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Settings</h1>
      </div>

      <div className="card">
        <h3>Account</h3>
        <div className="kv">
          <div className="muted small">Email</div><div>{user?.email}</div>
          <div className="muted small">Display name</div><div>{user?.displayName ?? '—'}</div>
        </div>
      </div>

      <div className="card">
        <h3>Your persona</h3>
        <p className="helper">How tweetvia tunes drafts to who you are. Update any time — applies to all future drafts.</p>
        {err && <div className="error">{err}</div>}
        <div className="persona-grid">
          {personas.map(p => (
            <label key={p.id} className={`persona-card ${selectedPersonas.has(p.id) ? 'selected' : ''}`}>
              <input type="checkbox" checked={selectedPersonas.has(p.id)} onChange={() => togglePersona(p.id)} />
              <div>
                <strong>{p.label}</strong>
                <div className="muted small">{p.short}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <label>Or describe yourself</label>
          <input
            value={personaCustom}
            onChange={e => { setPersonaCustom(e.target.value); setPersonaSaved(false); }}
            placeholder="What do you do, what do you write about?"
            maxLength={500}
          />
        </div>
        <div className="button-row end">
          <button className="primary" onClick={savePersona} disabled={personaBusy}>
            {personaBusy ? 'Saving…' : personaSaved ? 'Saved ✓' : 'Save persona'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="button-row between">
          <div>
            <h3 style={{ margin: 0 }}>Ranking Playbook overrides</h3>
            {playbook && <div className="muted small">Playbook v{playbook.version} · global rules below apply by default</div>}
          </div>
        </div>
        <p className="helper">Uncheck rules you want to ignore for your tweets. Anything you add in the bottom box is appended to your generation prompts.</p>

        {playbook && (
          <>
            <h4>Hard rules</h4>
            <div className="stack tight">
              {playbook.hardRules.map(r => (
                <label key={r.id} className="toggle-row">
                  <input type="checkbox" checked={!disabled.has(r.id)} onChange={() => toggle(r.id)} />
                  <span>{r.text}</span>
                </label>
              ))}
            </div>

            <h4>Principles</h4>
            <div className="stack tight">
              {playbook.softPrinciples.map(r => (
                <label key={r.id} className="toggle-row">
                  <input type="checkbox" checked={!disabled.has(r.id)} onChange={() => toggle(r.id)} />
                  <span>{r.text}</span>
                </label>
              ))}
            </div>

            <h4>Your additions</h4>
            <div className="field">
              <label>Dos (one per line)</label>
              <textarea value={extraDos} onChange={e => { setExtraDos(e.target.value); setSaved(false); }} placeholder="e.g. Mention specific founder examples" />
            </div>
            <div className="field">
              <label>Donts (one per line)</label>
              <textarea value={extraDonts} onChange={e => { setExtraDonts(e.target.value); setSaved(false); }} placeholder="e.g. No emoji" />
            </div>
          </>
        )}

        <div className="button-row end">
          <button className="primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : saved ? 'Saved ✓' : 'Save overrides'}
          </button>
        </div>
      </div>
    </div>
  );
}
